import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { ArrowRight, Trash2, Smartphone, Loader2 } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CaregiverToken {
  token: string;
  deviceInfo: string;
  nickname?: string;
  addedAt: string;
}

export default function CaregiverDelete() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, dir } = useTranslation();
  const { user } = useAuth();
  
  const [isDeleting, setIsDeleting] = useState(false);
  const [caregiver, setCaregiver] = useState<CaregiverToken | null>(null);
  const index = parseInt(searchParams.get("index") || "-1", 10);

  useEffect(() => {
    const fetchCaregiver = async () => {
      if (!user || index < 0) return;
      
      const { data } = await supabase
        .from('user_settings')
        .select('caregiver_push_tokens')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (data?.caregiver_push_tokens && Array.isArray(data.caregiver_push_tokens)) {
        const tokens = data.caregiver_push_tokens as unknown as CaregiverToken[];
        if (tokens[index]) {
          setCaregiver(tokens[index]);
        }
      }
    };
    
    fetchCaregiver();
  }, [user, index]);

  const getDeviceName = (userAgent: string): string => {
    if (userAgent.includes('iPhone')) return 'iPhone';
    if (userAgent.includes('iPad')) return 'iPad';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'Mac';
    if (userAgent.includes('Linux')) return 'Linux';
    return 'מכשיר לא ידוע';
  };

  const handleDelete = async () => {
    if (!user || index < 0) return;
    
    setIsDeleting(true);
    try {
      const { data } = await supabase
        .from('user_settings')
        .select('caregiver_push_tokens')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (data?.caregiver_push_tokens && Array.isArray(data.caregiver_push_tokens)) {
        const tokens = data.caregiver_push_tokens as unknown as CaregiverToken[];
        const updatedTokens = tokens.filter((_, i) => i !== index);
        
        const { error } = await supabase
          .from('user_settings')
          .update({ caregiver_push_tokens: updatedTokens as any })
          .eq('user_id', user.id);
        
        if (error) throw error;
        
        toast.success((t as any)('caregiverRemoved') || 'המטפל הוסר בהצלחה');
        navigate(-1);
      }
    } catch (error) {
      toast.error((t as any)('removeError') || 'שגיאה בהסרת המטפל');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!caregiver) {
    return (
      <MainLayout>
        <div className="p-4 md:p-6 max-w-lg mx-auto flex items-center justify-center min-h-[50vh]" dir={dir}>
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-lg mx-auto" dir={dir}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            data-scan-item
            className="rounded-full"
          >
            <ArrowRight className="w-6 h-6" />
          </Button>
          <h1 className="text-2xl font-bold">
            {(t as any)('confirmDeleteCaregiver') || 'מחיקת מטפל'}
          </h1>
        </div>

        <div className="bg-card rounded-xl border border-border p-6 space-y-6">
          {/* Caregiver info */}
          <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-xl">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="font-medium text-lg">
                {caregiver.nickname || getDeviceName(caregiver.deviceInfo)}
              </p>
              <p className="text-sm text-muted-foreground">
                {caregiver.addedAt 
                  ? new Date(caregiver.addedAt).toLocaleDateString()
                  : 'חובר לאחרונה'}
              </p>
            </div>
          </div>

          {/* Warning */}
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl">
            <p className="text-sm text-destructive">
              {(t as any)('confirmDeleteCaregiverDesc') || 'המטפל לא יקבל יותר עדכונים לטלפון. אפשר תמיד לחבר אותו מחדש דרך ההגדרות.'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleBack}
              data-scan-item
              className="flex-1"
              disabled={isDeleting}
            >
              {(t as any)('cancel') || 'ביטול'}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-scan-item
              className="flex-1 gap-2"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              {(t as any)('delete') || 'מחק'}
            </Button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
